import { useEffect, useState } from "react";
import { DownloadSimple, Export, PlusSquare, DotsThreeVertical, DeviceMobile } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { fetchPwaStatus, registerServiceWorker } from "@/lib/pwa";

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isSafari = () => /safari/i.test(navigator.userAgent) && !/crios|fxios|edgios|chrome/i.test(navigator.userAgent);

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3">
    <span className="font-mono text-[11px] font-bold h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">{n}</span>
    <span className="flex items-center gap-2 text-[13px] leading-snug"><Icon size={18} weight="bold" className="text-muted-foreground shrink-0" /><span>{children}</span></span>
  </li>
);

// Browser-specific "how to install" — iPhone and some Android/desktop browsers have no programmatic install prompt.
function HowToInstall({ open, onClose }) {
  const ios = isIos();
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="install-howto-dialog">
        <DialogHeader>
          <DialogTitle>Add COPPRROD to your home screen</DialogTitle>
          <DialogDescription>{ios ? "iPhone / iPad install in three taps" : "Install from the browser menu"}</DialogDescription>
        </DialogHeader>
        <ol className="space-y-3 py-1">
          {ios ? (<>
            {!isSafari() && <li className="text-[12px] text-[hsl(var(--tone-warn))] font-semibold" data-testid="install-howto-safari-hint">Open this page in Safari first — other iPhone browsers cannot add apps to the home screen.</li>}
            <Step n={1} icon={Export}>Tap the <b>Share</b> button (square with an arrow) at the bottom of Safari.</Step>
            <Step n={2} icon={PlusSquare}>Scroll the list and tap <b>Add to Home Screen</b>.</Step>
            <Step n={3} icon={DeviceMobile}>Tap <b>Add</b> — COPPRROD opens full-screen from your home screen and stays logged in.</Step>
          </>) : (<>
            <Step n={1} icon={DotsThreeVertical}>Open the browser menu (⋮ on Android Chrome, or the install icon in the address bar on desktop).</Step>
            <Step n={2} icon={DownloadSimple}>Tap <b>Install app</b> / <b>Add to Home screen</b>.</Step>
            <Step n={3} icon={DeviceMobile}>Confirm — COPPRROD opens like a normal app and stays logged in.</Step>
          </>)}
        </ol>
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="install-howto-close">Got it</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Header button for users the server-side PWA switch allows (admins + observers for now): Chrome/Edge → native install prompt;
// iPhone or no prompt → step-by-step instructions.
export function InstallAppButton() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [standalone, setStandalone] = useState(isStandalone);
  const [howto, setHowto] = useState(false);
  useEffect(() => {
    if (!user) { setEnabled(false); return undefined; }
    let alive = true;
    fetchPwaStatus().then((ok) => { if (alive) setEnabled(ok); });
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => { setPrompt(null); setStandalone(true); setHowto(false); toast.success("COPPRROD installed — open it from your home screen"); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { alive = false; window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, [user]);
  useEffect(() => {
    if (!enabled) return;
    registerServiceWorker();
    // the page's first manifest fetch happened before login (404) — re-attach the link so the browser fetches it with the session
    const l = document.querySelector('link[rel="manifest"]');
    if (l) { const c = l.cloneNode(); l.remove(); document.head.appendChild(c); }
  }, [enabled]);
  if (!enabled || standalone) return null;
  const install = async () => {
    if (prompt) {
      try {
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        setPrompt(null);                                   // a prompt event can be used once
        if (outcome === "accepted") return;
      } catch { setPrompt(null); }
    }
    setHowto(true);
  };
  return (
    <>
      <button onClick={install} data-testid="install-app-button" title="Install COPPRROD on this device" aria-label="Install COPPRROD app"
        className="h-8 px-2.5 flex items-center gap-1.5 rounded-md text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
        <DownloadSimple size={16} weight="bold" /><span className="hidden sm:inline">Install app</span>
      </button>
      <HowToInstall open={howto} onClose={() => setHowto(false)} />
    </>
  );
}
